"use client";

// MiniRouteMap — a stylized, REAL-geography route map for marketing
// surfaces. Replaces the old hand-placed dots-on-a-blank-panel with an
// actual dark map of central Japan (Kansai → Kanto), a route line that
// draws itself in when scrolled into view, and labeled city pins.
//
// Static by design: marketing needs a "travel memory" vignette, not a
// navigation app — no tiles, no interaction, no network. The coastline
// below is Natural Earth 50m data for the lng 133.2–141.8 / lat
// 32.6–37.4 window, projected (equirectangular) and simplified offline,
// then baked in as path constants. Same projection maps city lat/lng to
// the viewBox, so pins sit on true geography.

import { motion, useReducedMotion } from "framer-motion";

export type RouteCity = {
  name: string;
  lat: number;
  lng: number;
  /** Memories captured in this city — rendered as a soft count badge. */
  memories?: number;
  /** Label offset in viewBox px, to dodge coastline/route overlap. */
  labelDx?: number;
  labelDy?: number;
};

// ViewBox + the projection window the coastline was baked with. If you
// change the window, re-bake the paths — they must stay in sync.
const W = 360;
const H = 200;
const LNG0 = 133.2;
const LNG1 = 141.8;
const LAT0 = 32.6;
const LAT1 = 37.4;

function project(lng: number, lat: number): [number, number] {
  return [
    ((lng - LNG0) / (LNG1 - LNG0)) * W,
    ((LAT1 - lat) / (LAT1 - LAT0)) * H,
  ];
}

// Natural Earth 50m coastline, pre-projected. Rings that overflow the
// viewBox (Honshu continues north-east) are clipped by the SVG itself.
const JAPAN_PATHS = [
  "M7.1 49.8L5.2 51.4L1.6 50.9L-0.4 48.6L0.3 46.1L4.0 44.2L7.6 48.1L7.1 49.8Z",
  "M215.3 -17.6L211.4 -17.5L210.4 -17.9L212.8 -18.9L213.0 -20.7L214.4 -23.7L214.4 -24.6L211.2 -24.8L211.4 -28.3L213.8 -31.7L220.2 -37.1L222.0 -38.2L222.3 -35.8L220.3 -30.2L219.9 -28.2L225.0 -27.7L221.7 -21.0L215.3 -17.6Z",
  "M72.5 129.7L68.0 133.2L64.1 133.0L62.1 131.4L61.4 129.4L65.2 126.3L68.4 122.0L71.3 120.0L73.7 119.0L75.5 119.0L71.4 125.1L72.5 129.7Z",
  "M48.2 121.5L47.4 122.3L46.7 122.1L44.0 124.0L43.5 122.2L41.4 121.0L41.1 120.0L47.1 119.4L49.1 119.9L48.2 121.5Z",
  "M-39.1 144.0L-37.1 145.5L-32.2 144.9L-31.6 145.3L-33.0 146.7L-35.2 148.0L-39.0 147.0L-41.5 147.0L-41.8 144.7L-41.5 143.8L-39.1 144.0Z",
  "M48.5 131.0L60.2 132.2L60.9 142.4L61.7 148.0L56.5 152.9L46.3 161.4L42.1 168.9L38.7 171.4L27.4 162.8L18.1 162.0L1.7 172.9L-4.1 182.2L-7.7 184.0L-13.8 193.6L-21.3 192.7L-20.6 187.4L-29.5 186.8L-32.3 180.9L-30.3 175.8L-28.7 172.7L-31.6 170.6L-33.0 165.4L-38.5 166.0L-48.9 169.2L-38.2 163.8L-27.8 157.0L-21.0 150.4L-18.7 145.6L-15.1 140.8L-8.8 138.0L-2.8 144.7L4.1 143.0L11.4 142.8L17.9 138.8L16.9 131.5L21.2 131.8L31.3 127.2L42.7 128.4Z",
  "M336.1 -165.5L345.6 -166.9L343.3 -154.0L344.5 -138.5L349.2 -130.4L359.9 -120.5L365.7 -106.6L368.0 -99.7L367.5 -94.5L367.4 -84.5L364.2 -71.3L360.3 -68.4L357.6 -66.7L354.1 -65.6L352.6 -61.0L349.4 -56.8L347.8 -45.7L341.9 -40.8L331.0 -39.1L324.9 -31.2L323.5 -22.9L326.7 -12.4L326.6 11.9L322.1 19.8L317.8 23.0L310.9 37.4L310.6 42.3L308.7 48.7L310.7 55.9L318.7 67.4L311.4 72.4L303.8 78.7L302.1 88.9L299.5 92.4L291.3 96.0L283.0 102.2L278.1 103.5L278.1 99.6L278.4 90.3L280.7 85.6L285.8 79.8L288.7 75.6L284.1 72.2L277.7 72.6L275.0 77.1L274.9 79.4L270.6 86.7L273.9 89.5L271.0 93.8L266.4 89.9L258.0 87.6L249.6 91.2L247.6 96.0L246.4 106.7L242.1 112.6L236.0 115.9L232.8 112.5L234.5 101.0L235.3 96.0L225.1 96.4L222.3 100.5L215.5 106.3L208.8 116.8L195.2 114.5L181.8 114.0L161.7 117.4L171.1 112.4L170.6 109.5L163.1 110.0L159.3 107.7L156.3 107.7L155.4 112.1L154.2 108.1L152.9 100.9L152.9 97.5L148.3 99.0L141.4 108.8L143.0 117.1L154.1 123.6L152.9 128.2L140.0 130.9L128.4 137.7L113.7 159.9L94.3 160.3L89.9 153.3L82.7 145.9L81.0 134.1L80.9 128.5L88.3 124.3L92.6 118.9L90.2 114.4L77.1 115.4L66.3 110.5L57.9 109.6L48.7 111.5L42.2 112.6L32.2 119.7L20.0 121.4L11.5 123.7L5.7 125.6L-2.4 129.1L-17.8 131.0L-27.9 129.7L-37.1 128.1L-41.8 140.3L-44.1 148.4L-60.1 139.8L-72.2 140.9L-78.6 143.1L-85.8 142.7L-92.2 141.4L-96.7 130.8L-94.1 127.1L-86.6 124.7L-77.3 124.5L-70.5 118.7L-61.4 113.9L-51.8 107.9L-43.6 101.4L-32.9 93.5L-21.0 82.6L-11.6 78.7L2.8 76.8L9.9 80.3L17.4 78.7L27.6 79.4L42.4 77.5L52.6 73.8L82.6 68.9L86.5 69.9L85.1 75.3L89.0 78.1L103.8 79.0L113.2 74.7L121.2 68.0L117.5 58.7L123.8 49.0L132.2 43.3L146.4 27.4L147.3 8.4L157.5 -0.6L172.6 -5.1L173.2 -1.6L161.0 7.5L155.9 9.5L158.8 15.6L159.6 21.0L164.2 26.1L171.5 26.9L179.3 19.8L197.3 14.0L210.1 9.4L223.9 0.3L230.6 -6.7L235.2 -15.6L253.1 -25.4L259.6 -30.9L262.7 -41.7L267.1 -50.0L274.1 -57.8L279.6 -71.0L282.1 -78.0L285.1 -81.6L286.7 -86.0L286.9 -97.9L282.4 -103.5L276.7 -103.2L274.4 -106.6L279.4 -107.7L283.5 -114.0L285.3 -121.5L281.5 -130.6L283.3 -136.4L288.2 -139.5L293.1 -140.6L296.4 -143.6L299.0 -150.2L299.1 -158.5L303.1 -158.7L308.3 -158.8L311.4 -156.5L314.1 -144.1L318.2 -143.1L321.3 -147.1L331.5 -145.1L335.9 -149.5L336.7 -158.6L333.0 -159.9L329.5 -158.0L318.2 -160.6L322.0 -170.0L328.6 -169.8L336.1 -165.5Z",
];

// Route order = travel order = the order cities are passed in.
const DOT_COLORS = ["#fb8a7f", "#f8c465", "#7dd3fc"]; // coral, gold, cyan

/** Catmull-Rom spline through the city points → smooth cubic path. */
function routePath(pts: [number, number][]): string {
  if (pts.length < 2) return "";
  const p = (i: number) => pts[Math.max(0, Math.min(pts.length - 1, i))];
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = p(i - 1);
    const [x1, y1] = p(i);
    const [x2, y2] = p(i + 1);
    const [x3, y3] = p(i + 2);
    const c1x = x1 + (x2 - x0) / 6;
    const c1y = y1 + (y2 - y0) / 6;
    const c2x = x2 - (x3 - x1) / 6;
    const c2y = y2 - (y3 - y1) / 6;
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
  }
  return d;
}

export default function MiniRouteMap({ cities }: { cities: RouteCity[] }) {
  const reduceMotion = useReducedMotion();
  const pts = cities.map((c) => project(c.lng, c.lat));
  const route = routePath(pts);
  const label = `Route map of ${cities.map((c) => c.name).join(", ")}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="absolute inset-0 h-full w-full"
      role="img"
      aria-label={label}
    >
      {/* Landmass — calm, near-black, hairline coast. */}
      <g>
        {JAPAN_PATHS.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="#15181d"
            stroke="rgba(255,255,255,0.07)"
            strokeWidth={0.75}
          />
        ))}
      </g>

      {/* Route — dashed soft blue. The dash pattern can't coexist with
          framer's pathLength trick (it overwrites dasharray), so the
          draw-in animates a WIDER solid stroke inside a mask that
          reveals the dashed line beneath it. */}
      <mask id="route-reveal" maskUnits="userSpaceOnUse">
        <motion.path
          d={route}
          fill="none"
          stroke="#fff"
          strokeWidth={10}
          strokeLinecap="round"
          initial={reduceMotion ? false : { pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1.6, ease: "easeInOut", delay: 0.15 }}
        />
      </mask>
      <path
        d={route}
        fill="none"
        stroke="rgba(125,211,252,0.55)"
        strokeWidth={2}
        strokeDasharray="1 7"
        strokeLinecap="round"
        mask="url(#route-reveal)"
      />

      {/* City pins: soft halo + dot + halo label + memory badge. Fade
          in sequenced behind the route draw. */}
      {cities.map((c, i) => {
        const [x, y] = pts[i];
        const color = DOT_COLORS[i % DOT_COLORS.length];
        const dx = c.labelDx ?? 9;
        const dy = c.labelDy ?? -7;
        return (
          <motion.g
            key={c.name}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.6 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{
              delay: 0.3 + i * 0.45,
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{ transformOrigin: `${x}px ${y}px` }}
          >
            <circle cx={x} cy={y} r={9} fill={color} opacity={0.14} />
            <circle
              cx={x}
              cy={y}
              r={4.2}
              fill={color}
              stroke="#0b0b0d"
              strokeWidth={1.4}
            />
            <text
              x={x + dx}
              y={y + dy}
              fontSize={10.5}
              fontWeight={600}
              fill="#e4e4e7"
              stroke="#0b0b0d"
              strokeWidth={3}
              paintOrder="stroke"
              strokeLinejoin="round"
            >
              {c.name}
            </text>
            {typeof c.memories === "number" && (
              <text
                x={x + dx}
                y={y + dy + 11}
                fontSize={7.5}
                fontWeight={500}
                fill="#8e93a3"
                stroke="#0b0b0d"
                strokeWidth={2.5}
                paintOrder="stroke"
                strokeLinejoin="round"
              >
                {c.memories} memories
              </text>
            )}
          </motion.g>
        );
      })}
    </svg>
  );
}
